import { css, html, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';

import { TextArea } from '@material/mwc-textarea';
import { TextField } from '@material/mwc-textfield';
import { undefinedIfEmpty } from '../../helpers/utils';
import {
    BulkRegPointRequest, Location,
    ObjectId, PointLocationInputEvent,
    AddTagRequest, TopicCategory,
} from '../../types';
import { PointInput } from '../common/point-input';
import { getMainPage } from '../../helpers/page-name';


interface PointCountPair {
    point: BulkRegPointRequest;
    count: number;
}

@customElement('reg-add-point-component')
export class RegAddPointComponent extends LitElement {

    @query('#name')
    private nameElement!: TextField;

    @query('#url')
    private readonly urlElement!: TextField;

    @query('#description')
    private readonly descriptionElement!: TextArea;

    @query('#pointInput')
    private readonly pointInputElement!: PointInput;

    @property({ type: Array })
    categories: TopicCategory[] = [];

    @property({ type: Array })
    emails: string[] = [];

    @state()
    pointsToAdd: BulkRegPointRequest[] = [];

    @state()
    pointsCountMap: Map<number, PointCountPair> = new Map();

    curPointId: number = 0;

    protected render() {
        return html`
            <div class="container">
                <vaadin-accordion>
                    ${[...this.pointsCountMap.entries()].map(this.renderPoint)}
                </vaadin-accordion>
                <hr />
                <hr />
                <p>
                    ADD POINTS HERE. The topic for each email will receive the same set of points.
                </p>
                <point-input
                    id="pointInput"
                    .categories="${this.categories}"
                    @location-changed="${this.locationChanged}"
                >
                </point-input>
                <mwc-textfield
                    id="name"
                    class="input"
                    label="Title"
                    charcounter
                    maxlength="80"
                ></mwc-textfield>

                <vaadin-details>
                    <div slot="summary"><h4>Add Details</h4></div>

                    <mwc-textfield
                        id="url"
                        class="input"
                        label="URL"
                        helper="Must be a full URL (including 'https://')."
                        charcounter
                        maxlength="2000"
                    >
                    </mwc-textfield>

                    <br />

                    <mwc-textarea
                        id="description"
                        class="input"
                        label="Description"
                        charcounter
                        maxlength="3000"
                    ></mwc-textarea>
                </vaadin-details>

                <div
                    style="display: flex; flex-direction: row; margin-left: 10px; margin-top:15px; "
                >
                    <mwc-button class="button addPoint" @click="${this.addPoint}" raised
                        >Add Point</mwc-button
                    >
                </div>

                <error-toast id="errorToast"></error-toast>
            </div>
        `;
    }

    renderPoint = (entry: [number, PointCountPair]) => {
        const pointId = entry[0];
        const point = entry[1].point;
        const pointCount = entry[1].count;

        return html`
            <vaadin-accordion-panel>
                <div slot="summary">
                    <div class="pointTracker">
                        <p>${point.name} | Email Count: ${pointCount}</p>
                        <mwc-icon-button
                            class="closeButton"
                            icon="close"
                            @click="${() => this.removePoint(pointId)}"
                        >
                        </mwc-icon-button>
                    </div>
                </div>
                <div class="container">
                    <span>Category: ${point.catName}</span>
                    <span>Location: ${point.location.search}</span>
                </div>
            </vaadin-accordion-panel>
        `;
    };

    static get styles() {
        return css`
            .input {
                width: 100%;
                padding: auto 5px;
            }

            .button {
                width: 100px;
                margin: 0px;
                margin-left: 10px;
                color: var(--theme-white-color);
            }

            .save {
                --mdc-theme-primary: var(--main-success-color);
            }

            .button.addPoint {
                --mdc-theme-primary: var(--main-primary-color);
            }

            .container {
                display: flex;
                flex-direction: column;
                max-width: 800px;
            }

            .pointTracker {
                display: flex;
                flex-direction: row;
                width: 100%;
            }
        `;
    }

    private locationChanged(evt: CustomEvent<PointLocationInputEvent>) {
        this.nameElement.value = evt.detail.name ?? '';
    }

    private addPoint() {
        // Using a bunch of variables to avoid short circuiting (we want to call `validate()` on all of the elements).
        const locationValid = this.pointInputElement.locationIsValid;
        const topicCategoryValid = this.pointInputElement.topicCategoryIsValid;
        const nameValid = this.nameElement.reportValidity();
        const urlValid = this.urlElement.reportValidity();
        const descriptionValid = this.descriptionElement.reportValidity();

        const valid =
            locationValid && topicCategoryValid && nameValid && urlValid && descriptionValid;

        if (!valid) {
            getMainPage().errorToastElement.showToastAndThrow(
                'Please fix your invalid inputs (marked red).'
            );
        }

        // Geocode Location Result only set after successful geocoding, so we can set this as the default case and fallback to the locationElement.
        const location: Location = this.pointInputElement.locationValue;

        // NOTE: This is a bit tricky: we want this to be undefined if it is undefined or '', so that it will not
        // even get serialized to the database.
        const name = undefinedIfEmpty(this.nameElement.value?.trim());
        const url = undefinedIfEmpty(this.urlElement.value?.trim());
        const description = this.descriptionElement.value?.trim();
        const catName = this.pointInputElement.topicCategoryValue;

        const points: BulkRegPointRequest[] = [];
        const tag_info: AddTagRequest = {
            user_tags: [],
        };

        this.emails.forEach((email) => {
            points.push({
                _id: undefined,
                bulkCounter: this.curPointId,
                email,
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                topic_id: <ObjectId>{},
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                topic_category_id: <ObjectId>{},
                catName,
                location,
                name,
                description,
                special_offer: undefined,
                start: undefined,
                end: undefined,
                source: undefined,
                embedded_video: undefined,
                custom_icon_url: undefined,
                // These should only be set by topic admins from the command center, so no need to set them here.
                // It is an error for a non-admin to set these.
                active: undefined,
                featured: undefined,
                tag_info,
                is_gate_hidden: undefined,
                custom_information: undefined,
                url_images: undefined,
                url,
                number: undefined,
                custom_shape: undefined
            });
        });

        this.curPointId++;

        this.pointsToAdd = this.pointsToAdd.concat(points);
        this.pointsCountMap = this.computePointCountMap();
    }

    private removePoint(id: number) {
        this.pointsToAdd = this.pointsToAdd.filter((p) => p.bulkCounter !== id);
        this.pointsCountMap = this.computePointCountMap();
    }

    private computePointCountMap(): Map<number, PointCountPair> {
        const result: Map<number, PointCountPair> = new Map();
        this.pointsToAdd.forEach((point) => {
            const id = point.bulkCounter;
            const numPointsForName = result.get(id)?.count ?? 0;
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            result.set(id, {
                point,
                count: numPointsForName + 1,
            } as PointCountPair);
        });

        return result;
    }
}
